import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, PlusIcon, DownloadIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
interface ListItem {
  id: string;
  name: string;
  category: string;
  description: string;
  status: 'active' | 'inactive';
  createdDate: string;
  lastModified: string;
}
const listItems: ListItem[] = [
// Jurisdictions
{
  id: '1',
  name: 'Central Business District',
  category: 'Jurisdictions',
  description: 'Main commercial area jurisdiction',
  status: 'active',
  createdDate: '2024-01-15',
  lastModified: '2024-01-20'
}, {
  id: '2',
  name: 'Residential Zone A',
  category: 'Jurisdictions',
  description: 'Northern residential area',
  status: 'active',
  createdDate: '2024-01-10',
  lastModified: '2024-01-12'
},
// Organizations
{
  id: '3',
  name: 'Municipal Health Department',
  category: 'Organizations',
  description: 'Public health services organization',
  status: 'active',
  createdDate: '2024-01-18',
  lastModified: '2024-01-18'
}, {
  id: '4',
  name: 'Environmental Protection Agency',
  category: 'Organizations',
  description: 'Environmental regulation body',
  status: 'inactive',
  createdDate: '2024-01-22',
  lastModified: '2024-01-22'
},
// Agencies
{
  id: '5',
  name: 'Commerce Department',
  category: 'Agencies',
  description: 'Oversees business licensing and commercial activities',
  status: 'active',
  createdDate: '2024-01-05',
  lastModified: '2024-01-05'
}, {
  id: '6',
  name: 'Public Works Department',
  category: 'Agencies',
  description: 'Manages infrastructure and public facilities',
  status: 'active',
  createdDate: '2024-01-08',
  lastModified: '2024-01-10'
},
// Categories
{
  id: '7',
  name: 'Business & Licensing',
  category: 'Categories',
  description: 'Services related to business operations and licensing',
  status: 'active',
  createdDate: '2024-01-12',
  lastModified: '2024-01-12'
}, {
  id: '8',
  name: 'Property Services',
  category: 'Categories',
  description: 'Services related to property management and development',
  status: 'active',
  createdDate: '2024-01-14',
  lastModified: '2024-01-14'
},
// Reason Codes
{
  id: '9',
  name: 'Missing Documentation',
  category: 'Reason Codes',
  description: 'Required documents not provided with application',
  status: 'active',
  createdDate: '2024-01-25',
  lastModified: '2024-01-25'
}, {
  id: '10',
  name: 'Non-Compliant',
  category: 'Reason Codes',
  description: 'Application does not meet regulatory requirements',
  status: 'active',
  createdDate: '2024-01-26',
  lastModified: '2024-01-26'
}];
const StatusBadge = ({
  status
}: {
  status: ListItem['status'];
}) => {
  const styles = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-red-100 text-red-800'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-4101">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>;
};
export const ListsPage = () => {
  const {
    navigate
  } = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSections, setExpandedSections] = useState<string[]>(['Jurisdictions', 'Organizations', 'Agencies', 'Categories', 'Reason Codes']);
  const rowsPerPage = 10;
  const handleNavigateToAddList = () => {
    navigate('/lists/add');
  };
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setCurrentPage(1);
  };
  const toggleSection = (section: string) => {
    setExpandedSections(current => current.includes(section) ? current.filter(s => s !== section) : [...current, section]);
  };
  const filteredItems = listItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });
  // Group items by category
  const itemsByCategory = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, ListItem[]>);
  const totalPages = Math.ceil(filteredItems.length / rowsPerPage);
  const paginatedItems = filteredItems.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  return <div className="min-h-screen bg-gray-50" data-id="element-4102">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-4103">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-4104">
          <a href="/" className="hover:text-gray-700" data-id="element-4105">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-4106" />
          <span className="text-gray-900" data-id="element-4107">Service Catalogue</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-4108">
          <div className="flex justify-between items-start" data-id="element-4109">
            <div data-id="element-4110">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-4111">
                Service Catalogue
              </h1>
              <p className="text-gray-600" data-id="element-4112">
                Browse and manage jurisdictions, organizations, agencies,
                categories, and reason codes
              </p>
            </div>
            <div className="flex gap-3" data-id="element-4113">
              <button onClick={handleNavigateToAddList} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-4114">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-4115" />
                New Service Item
              </button>
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-4116">
                <DownloadIcon className="h-4 w-4 mr-2" data-id="element-4117" />
                Export
              </button>
            </div>
          </div>
        </div>
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200" data-id="element-4118">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-4119">
            <div className="flex-1 relative" data-id="element-4120">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-4121" />
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4122" />
            </div>
            <div className="w-full md:w-64" data-id="element-4123">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-4124">
                <option value="all" data-id="element-4125">All Categories</option>
                <option value="Jurisdictions" data-id="element-4126">Jurisdictions</option>
                <option value="Organizations" data-id="element-4127">Organizations</option>
                <option value="Agencies" data-id="element-4128">Agencies</option>
                <option value="Categories" data-id="element-4129">Categories</option>
                <option value="Reason Codes" data-id="element-4130">Reason Codes</option>
              </select>
            </div>
            <div className="w-full md:w-64" data-id="element-4131">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-4132">
                <option value="all" data-id="element-4133">All Statuses</option>
                <option value="active" data-id="element-4134">Active</option>
                <option value="inactive" data-id="element-4135">Inactive</option>
              </select>
            </div>
            <button onClick={handleClearFilters} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4136">
              Clear Filters
            </button>
          </div>
        </div>
        {/* Service Catalogue */}
        <div className="space-y-8" data-id="element-4137">
          {Object.keys(itemsByCategory).length > 0 ? Object.entries(itemsByCategory).map(([category, items]) => <div key={category} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200" data-id="element-4138">
                <button className="flex items-center justify-between w-full text-left mb-4" onClick={() => toggleSection(category)} data-id="element-4139">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-4140">
                    {category}
                  </h3>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${expandedSections.includes(category) ? 'rotate-180' : ''}`} data-id="element-4141" />
                </button>
                {expandedSections.includes(category) && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4142">
                    {items.map(item => <div key={item.id} className="p-5 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200 group relative overflow-hidden bg-white flex flex-col" data-id="element-4143">
                        <div className="flex-1" data-id="element-4144">
                          <div className="flex items-center justify-between mb-2" data-id="element-4145">
                            <h4 className="text-lg font-medium text-gray-900 group-hover:text-blue-700 transition-colors duration-200" data-id="element-4146">
                              {item.name}
                            </h4>
                            <StatusBadge status={item.status} data-id="element-4147" />
                          </div>
                          <p className="text-sm text-gray-600 mb-4" data-id="element-4148">
                            {item.description}
                          </p>
                          <div className="text-xs text-gray-500 space-y-1 mb-4" data-id="element-4149">
                            <div data-id="element-4150">
                              Created:{' '}
                              {new Date(item.createdDate).toLocaleDateString()}
                            </div>
                            <div data-id="element-4151">
                              Modified:{' '}
                              {new Date(item.lastModified).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <button onClick={() => navigate(`/lists/edit?id=${item.id}`)} className="w-full mt-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-150" data-id="element-4152">
                          View Details
                          <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-4153" />
                        </button>
                      </div>)}
                  </div>}
              </div>) : <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-200 text-center" data-id="element-4154">
              <p className="text-gray-500" data-id="element-4155">
                No services found matching your criteria.
              </p>
            </div>}
        </div>
        {/* Pagination */}
        {filteredItems.length > 0 && <div className="mt-6 bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between" data-id="element-4156">
            <div data-id="element-4157">
              <p className="text-sm text-gray-700" data-id="element-4158">
                Showing{' '}
                <span className="font-medium" data-id="element-4159">
                  {filteredItems.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}
                </span>{' '}
                to{' '}
                <span className="font-medium" data-id="element-4160">
                  {Math.min(currentPage * rowsPerPage, filteredItems.length)}
                </span>{' '}
                of <span className="font-medium" data-id="element-4161">{filteredItems.length}</span>{' '}
                results
              </p>
            </div>
            <div data-id="element-4162">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" data-id="element-4163">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4164">
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === i + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-4165">
                    {i + 1}
                  </button>)}
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4166">
                  Next
                </button>
              </nav>
            </div>
          </div>}
      </div>
    </div>;
};